import { useQuery } from '@apollo/client'
import {
  ChangeEvent,
  RefObject,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'
import { ReactSVG } from 'react-svg'
import {
  useDebounceValue,
  useMediaQuery,
  useOnClickOutside,
} from 'usehooks-ts'

import { useOverlayStore } from '@features/overlay'
import useSearchModal from '@features/search/hooks/useSearchModal'
import { GQL_SCOPED_SEARCH_PRODUCTS } from '@features/search/model/scopedSearchQuery'
import { useSearchHistoryStore } from '@features/search/model/useSearchStore'
import { useAddressStore } from '@features/shipping-methods'

import type { SearchProductsQuery } from '@shared/generated'
import { SearchProductsDocument } from '@shared/generated'
import { cn } from '@shared/lib/cn'
import BackButton from '@shared/ui/back-button'
import Button from '@shared/ui/button'
import Input from '@shared/ui/input'

import { SearchResultsPopover } from './search-results-popover'

import styles from './styles.module.scss'



interface SearchInputProps {
  className?: string;
  showResults?: boolean;
  openModalAfterFocus?: boolean;
  inModal?: boolean;
  focusAfterMount?: boolean;
  /*
    Scope overrides used by a landing's custom header: the landing narrows search
    to its own assortment. `filter` is the contract field; `categoryId` is a
    fallback when only a category is configured. Both are also carried into the
    /search navigation so the results page keeps the same scope.
  */
  placeholder?: string;
  filter?: Record<string, unknown> | null;
  categoryId?: string | null;
}

export default function SearchInput (props: SearchInputProps) {
  const { t: tc } = useTranslation('common')
  const isTablet = useMediaQuery('(max-width: 1180px)')
  const {
    className,
    openModalAfterFocus = true,
    showResults = false,
    inModal,
    focusAfterMount,
    placeholder,
    filter,
    categoryId,
  } = props
  const [ open, setOpen ] = useState<boolean>(false)
  const [ inputValue, setInputValue ] = useState('')
  const [ debouncedValue ] = useDebounceValue(inputValue, 500)
  const rootRef = useRef<HTMLDivElement>(null)
  const { open: overlayOpen, close: overlayClose } = useOverlayStore()
  const { addQuery } = useSearchHistoryStore()
  const { closeSearchModal } = useSearchModal()

  const navigate = useNavigate()
  const inputRef = useRef<HTMLInputElement>(null)
  const { shopId } = useAddressStore()

  // A scope (landing custom header) needs arguments the generated document does
  // not declare, so the scoped variant is used only when one is configured.
  const hasScope = Boolean(filter || categoryId)

  const { data: searchData, loading: searchLoading } = useQuery<SearchProductsQuery>(
    hasScope ? GQL_SCOPED_SEARCH_PRODUCTS : SearchProductsDocument,
    {
      skip: debouncedValue.trim() === '',
      variables: {
        shopId: shopId,
        searchQuery: {
          search: debouncedValue,
        },
        pager: {
          limit: 4,
          page: 1,
        },
        ...(hasScope
          ? { filter: filter ?? undefined, categoryId: categoryId ?? undefined }
          : {}),
      },
    },
  )

  const closePopover = () => {
    setOpen(false)
    overlayClose()
  }

  useOnClickOutside(rootRef as RefObject<HTMLDivElement>, closePopover)

  useEffect(() => {
    if (open) {
      overlayOpen()
    }
    else {
      overlayClose()
    }
  }, [ open ])

  const HAS_RESULTS = useMemo(() => {
    return !!searchData?.searchProducts?.edges?.length
  }, [ searchData ])

  const IS_EMPTY = useMemo(() => {
    return !!(
      searchData?.searchProducts?.edges?.length === 0 && debouncedValue
    )
  }, [ searchData, debouncedValue ])

  useEffect(() => {
    if (focusAfterMount) {
      inputRef.current?.focus()
    }
  }, [])

  // const categoryId =
  //   searchData?.searchProducts?.edges?.[0]?.categories?.[0]?.id;
  useEffect(() => {
    if (!isTablet) {
      closeSearchModal()
    }
    else {
      setOpen(false)
    }
  }, [ isTablet ])

  const handleSearch = (value?: string) => {
    const query = (value ?? inputValue).trim()

    if (!query) return

    if (query.length > 3) {
      addQuery(query)
    }

    /*
      The scope applies to the suggest dropdown only. The results page reads just
      `query` (see pages/search) and would need its own scoping support to keep a
      landing's narrowing after submit.
    */
    navigate(`/search?query=${query}`)
    closePopover()
    setInputValue('')
    inputRef.current?.blur()
  }

  const { openSearchModal } = useSearchModal()

  return (
    <div
      className={cn(styles.root, className, {
        [styles.active]: open,
        [styles.inModal]: inModal,
      })}
      ref={rootRef}
    >
      <div className={styles.root__top}>
        {inModal && (
          <BackButton
            className={styles.root__back}
            onClick={closeSearchModal}
          />
        )}
        <Input
          ref={inputRef}
          className={cn(styles.input, {
            [styles.active]: open,
            [styles.inModal]: inModal,
          })}
          inputClassName={styles.inputElement}
          placeholder={placeholder || tc('search.button')}
          // theme="grey"
          // inputSize="large"
          value={inputValue}
          onInput={(e: ChangeEvent<HTMLInputElement>) => {
            setInputValue(e.target.value)
          }}
          onKeyDown={(e) => {
            if (e.code === 'Enter' || e.code === 'NumpadEnter') {
              e.preventDefault()
              handleSearch()

              if (isTablet) {
                closeSearchModal()
              }
            }
          }}
          onFocus={() => {
            if (isTablet && openModalAfterFocus) {
              openSearchModal()
              return
            }

            if (!isTablet) {
              setOpen(true)
            }
          }}
          startDecoratorClassName={styles.startDecorator}
          startDecorator={
            !isTablet ? (
              <button
                className={cn(
                  'reset-button',
                  styles.startDecoratorButton,
                  className,
                )}
                type="submit"
                onClick={() => handleSearch()}
              >
                <ReactSVG src="/icons/search.svg" wrapper="span" />
              </button>
            ) : null
          }
          endDecoratorClassName={styles['decorator-end']}
          endDecorator={
            <>
              {inputValue && (
                <button
                  className={cn('reset-button', styles.clear)}
                  type="button"
                  onClick={() => {
                    setInputValue('')
                  }}
                >
                  <ReactSVG
                    className={styles.clear}
                    src="/icons/cross.svg"
                    wrapper="span"
                  />
                </button>
              )}
              <Button
                className={styles.submit}
                type="submit"
                onClick={() => {
                  handleSearch()
                  if (isTablet) {
                    closeSearchModal()
                  }
                }}
              >
                {isTablet ? (
                  <ReactSVG src="/icons/search.svg" wrapper="span" />
                ) : (
                  tc('find')
                )}
              </Button>
            </>
          }
        />
      </div>

      {/*{(open || showResults) &&
        (IS_EMPTY || HAS_RESULTS || !!history.length) && ( */}

      {(open || showResults) && searchData && (
        <SearchResultsPopover
          searchData={searchData}
          searchLoading={searchLoading}
          IS_EMPTY={IS_EMPTY}
          HAS_RESULTS={HAS_RESULTS}
          onClose={closePopover}
          onSearch={handleSearch}
          onHistoryClick={(val) => {
            handleSearch(val)
          }}
          inModal={inModal}
        />
      )}
    </div>
  )
}
